<template>
  <section
    class="slider-section page-section"
    :class="[
      ui?.root,
      {
        white: variant === 'white',
        blue: variant === 'blue',
      },
    ]"
  >
    <UContainer :class="containerClass">
      <div
        v-if="showHeader"
        class="section-header mb-10 max-xl:mb-7 max-sm:mb-5"
        :class="ui?.header"
        data-slot="header"
      >
        <h2
          class="text-size-h2 font-semibold"
          :class="[
            ui?.title,
            {
              'text-white': variant === 'blue',
            },
          ]"
        >
          <slot name="title">{{ title }}</slot>
        </h2>

        <div
          class="section-navigation flex items-center justify-between max-sm:hidden"
          :class="ui?.navigation"
        >
          <slot name="all-button">
            <UButton
              v-if="allButton"
              class="mr-8 border-accent-default border ring-0 shrink-0"
              trailing-icon="i-local-move-up-right"
              variant="outline"
              :to="allButton?.to"
            >
              {{ allButton?.label }}
            </UButton>
          </slot>

          <template v-if="data?.length">
            <UButton
              class="mr-3"
              :class="ui?.prevButton"
              icon="i-local-chevron-right"
              :variant="swiperInstance?.isBeginning ? 'outline' : 'solid'"
              :disabled="swiperInstance?.isBeginning"
              :ui="{
                leadingIcon: 'rotate-180 text-button-foreground',
              }"
              :color="variant === 'blue' ? 'white' : undefined"
              @click="void swiperInstance?.slidePrev()"
            />
            <span
              v-if="pagination"
              class="mr-3 min-w-14 text-center text-neutral-default"
              :class="[
                ui?.counter,
                {
                  'text-white': variant === 'blue',
                },
              ]"
            >
              {{ counter }}
            </span>
            <UButton
              :class="ui?.nextButton"
              icon="i-local-chevron-right"
              :variant="swiperInstance?.isEnd ? 'outline' : 'solid'"
              :disabled="swiperInstance?.isEnd"
              :color="variant === 'blue' ? 'white' : undefined"
              @click="void swiperInstance?.slideNext()"
            />
          </template>
        </div>
      </div>

      <ClientOnly>
        <Swiper
          v-if="!data?.length && !isMobile"
          class="max-md:hidden!"
          :class="ui?.swiper"
          :modules="[Grid]"
          :slides-per-view="slidesPerViewProxy"
          :slides-per-group="slidesPerGroupProxy"
          :space-between="slidesGap"
          :grid="gridProxy"
          @swiper="swiperInstance = $event"
        >
          <SwiperSlide v-for="(_, index) in 3" :key="index">
            <div
              class="w-full flex gap-5"
              :class="ui.loader"
              data-slot="loader"
            >
              <SPSkeleton :border="!!loaderBorder" variant="card" />
            </div>
          </SwiperSlide>
        </Swiper>

        <Swiper
          v-else-if="!isMobile"
          class="max-md:hidden!"
          :class="ui?.swiper"
          :modules="[Grid]"
          :slides-per-view="slidesPerViewProxy"
          :slides-per-group="slidesPerGroupProxy"
          :space-between="slidesGap"
          :grid="gridProxy"
          @swiper="swiperInstance = $event"
        >
          <SwiperSlide v-if="$slots['featured-slide']" class="featured-slide">
            <slot
              name="featured-slide"
              v-bind="{ slide: data[0]!, index: 0, data }"
            />
          </SwiperSlide>
          <SwiperSlide
            v-for="(slide, index) in $slots['featured-slide']
              ? data.slice(1)
              : data"
            :key="index"
          >
            <slot name="default" v-bind="{ slide, index, data }" />
          </SwiperSlide>
        </Swiper>
      </ClientOnly>

      <CardsSlider class="md:hidden">
        <slot
          v-for="(slide, index) in data"
          name="default"
          v-bind="{ slide, index, data }"
        />
      </CardsSlider>

      <UButton
        v-if="mobileAllButton && isLessThan('md')"
        class="w-full mt-5"
        trailing-icon="i-local-move-up-right"
        variant="outline"
        :to="allButton?.to"
      >
        {{ allButton?.label }}
      </UButton>
    </UContainer>
  </section>
</template>

<script lang="ts" setup generic="T = CardData">
import type { SwiperClass } from 'swiper/react'
import type { GridOptions } from 'swiper/types'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Grid } from 'swiper/modules'
import type { CardData } from '~/types'

defineOptions({
  name: 'SPSliderSection',
})

type DefSlotPayload = {
  slide: T
  data: T[]
  index: number
}

defineSlots<{
  default: (props: DefSlotPayload) => any
  title: () => void
  'all-button': () => any
  'featured-slide': (props: DefSlotPayload) => any
}>()

const props = withDefaults(
  defineProps<{
    title?: string
    data?: T[]
    variant?: 'white' | 'blue'
    slidesPerView?: number
    slidesPerGroup?: number
    pagination?: boolean
    containerClass?: string
    loading?: boolean
    skeletonAmount?: number
    loaderBorder?: boolean
    grid?: GridOptions
    showHeader?: boolean
    allButton?: {
      label: string
      to: string
    }
    mobileAllButton?: boolean
    ui?: {
      root?: string
      header?: string
      title?: string
      navigation?: string
      prevButton?: string
      nextButton?: string
      counter?: string
      swiper?: string
      loader?: string
    }
  }>(),
  {
    variant: undefined,
    title: '',
    data: () => [],
    slidesPerView: undefined,
    slidesPerGroup: undefined,
    loading: false,
    skeletonAmount: 3,
    loaderBorder: false,
    grid: undefined,
    pagination: true,
    containerClass: '',
    showHeader: true,
    mobileAllButton: false,
    ui: () => ({}),
  },
)

const slots = useSlots()

const breakpoints = useBreakpointsStore()
const { isBigTablet, isMobile, isUltrasmall, isLessThanSm, isLessThanLg } =
  storeToRefs(breakpoints)
const { isLessThan } = breakpoints

const swiperInstance = ref<SwiperClass | null>(null)

const isBigTabletVal = ref(isBigTablet.value)

onMounted(() => {
  nextTick(() => swiperInstance.value?.update())
})

const slidesGap = computed(() => {
  if (isBigTabletVal.value) {
    return 16
  }

  if (isMobile.value) {
    return 8
  }

  return 20
})

const slidesPerViewProxy = computed(() => {
  if (slots['featured-slide']) {
    return 'auto'
  }

  if (props.slidesPerView) {
    return props.slidesPerView
  }

  if (isUltrasmall.value) {
    return 1.7
  }

  if (isMobile.value) {
    return 2.1
  }

  if (isLessThanLg.value) {
    return 2.32
  }

  return 3
})

const slidesPerGroupProxy = computed(() => {
  if (isLessThanSm.value) {
    return 2
  }

  return props.slidesPerGroup
})

const gridProxy = computed<GridOptions>(() => {
  if (props.grid) {
    return props.grid
  }

  if (isLessThanSm.value) {
    return {
      rows: 2,
      fill: 'row',
    }
  }

  return {
    rows: 1,
  }
})

const counter = computed(() => {
  if (typeof slidesPerViewProxy.value === 'string') {
    return `${slidesPerGroupProxy.value} / ${props.data.length}`
  }

  return `${Math.round(slidesPerViewProxy.value)} / ${props.data?.length}`
})
</script>

<style lang="scss" scoped>
.slider-section {
  // --margin-x: 20px;
  // width: calc(100dvw - var(--margin-x) * 2);
  margin-left: auto;
  margin-right: auto;
  // padding-top: 80px;
  // padding-bottom: 80px;
  border-radius: 30px;

  &.white {
    background-color: var(--color-white);
  }

  &.blue {
    background-color: var(--color-accent-default);
  }
}

.section-header {
  display: flex;
  justify-content: space-between;
}

:deep(.swiper-wrapper) {
  width: 100%;
}
</style>
